// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getOrderState, OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { OrderDetailsPageContext } from "@insite/content-library/Pages/OrderDetailsPage";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadSignaturePodImage from "@mcmc/App/Handlers/OrderDetails/loadSignaturePodImage";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getsShareTrackingRecords } from "@mcmc/App/Store/Selectors/ShareTrackingRecordsSelectors";
import { getSignaturePodImg } from "@mcmc/App/Store/Selectors/SignaturePodImageSelector";
import { orderInformationSectionGlobalStyles } from "@mcmc/App/WidgetExtensions/AccountPagesGlobalStyle";
import LazyImage from "@mcmc/MobiusOverrides/LazyImage";
import Notify from "@mcmc/XDL/Components/Notify";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: McMcApplicationState) => {
    const location = getLocation(state);
    let orderNumber;
    if (location && location.search) {
        const parsedQuery = parseQueryString<{ orderNumber: string }>(location.search);
        orderNumber = parsedQuery.orderNumber;
    }

    const order = getOrderState(state, state.pages.orderDetails.orderNumber).value;
    const apiOrderNumber = order?.erpOrderNumber || order?.webOrderNumber || "";

    return {
        shareTrackingCollectionDataView: getsShareTrackingRecords(state, orderNumber),
        signaturePodImg: getSignaturePodImg(state, apiOrderNumber),
    };
};

const mapDispatchToProps = {
    loadSignaturePodImage,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderApprovalDetailsOrderInformationStyles {
    orderInformationGridContainer?: GridContainerProps;
    itemInformationGridItem?: GridItemProps;
    titleText?: TypographyProps;
    pdfItemInformationGridItem?: GridItemProps;
    fullWidthInformationGridItem?: GridItemProps;
    signatureImgLink?: LinkPresentationProps;
    modal?: ModalPresentationProps;
    imageWrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const orderInformationStyles: OrderApprovalDetailsOrderInformationStyles = {
    orderInformationGridContainer: {
        gap: 20,
        css: css`
            margin-bottom: 30px;
        `,
    },
    itemInformationGridItem: orderInformationSectionGlobalStyles.itemGrid,
    titleText: orderInformationSectionGlobalStyles.itemTitleText,
    pdfItemInformationGridItem: {
        ...orderInformationSectionGlobalStyles.itemGrid,
        width: 12,
        css: css`
            display: none;

            @media print {
                display: block;
            }
        `,
    },
    fullWidthInformationGridItem: {
        width: 12,
        css: css`
            ${orderInformationSectionGlobalStyles.itemGrid!.css};
            display: block;
        `,
    },
    signatureImgLink: {
        css: css`
            ${getProp("theme.link.defaultProps.css")};
            @media print {
                display: none;
            }
        `,
    },
    modal: {
        size: 600,
        cssOverrides: {
            modalContent: css`
                ${getProp("theme.modal.defaultProps.cssOverrides.modalContent")}
                min-height: 300px;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

const styles = orderInformationStyles;

const OrderDetailsOrderInformation: React.FC<Props> = ({
    shareTrackingCollectionDataView,
    signaturePodImg,
    loadSignaturePodImage,
}: Props) => {
    const { value: order } = useContext(OrderStateContext);
    const sharedTrackingData = shareTrackingCollectionDataView?.[0];
    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    if (!order) {
        return null;
    }

    const showSignatureImageClickHandler = () => {
        // if (!signaturePodImg) {
        //     loadSignaturePodImage({
        //         orderNo: order?.erpOrderNumber,
        //     });
        // }
        setModalIsOpen(true);
    };

    const ImageModalcancelHandler = () => {
        setModalIsOpen(false);
    };

    // <XNG-Changes>
    const getImgData = (img?: string) => {
        if (!img) {
            return "";
        }

        if (img.includes("data:image/jpeg;base64,")) {
            return img;
        }

        return `data:image/jpeg;base64,${img}`;
    };
    // </XNG-Changes>

    const showPOD = ["3", "4", "5"].indexOf(order.status) > -1 && order.properties.transactionType !== "rm";
    const podSignatureImg = getImgData(order?.properties.podImageBase64);
    return (
        <GridContainer data-test-selector="orderApprovalDetails_information" {...styles.orderInformationGridContainer}>
            {order.properties.orderDate && (
                <GridItem {...styles.itemInformationGridItem}>
                    {createWidgetElement("OrderDetails/OrderDate", {} as any)}
                </GridItem>
            )}
            {order.status && (
                <GridItem {...styles.itemInformationGridItem}>
                    {createWidgetElement("OrderDetails/OrderDetailsStatus", {} as any)}
                </GridItem>
            )}
            {order.customerPO && (
                <GridItem {...styles.itemInformationGridItem}>
                    {createWidgetElement("OrderDetails/OrderPONumber", {} as any)}
                </GridItem>
            )}
            {order.properties.placedBy && (
                <GridItem {...styles.itemInformationGridItem}>
                    {createWidgetElement("OrderDetails/OrderPlacedBy", {} as any)}
                </GridItem>
            )}
            {order.properties.promiseDate && (
                <GridItem {...styles.itemInformationGridItem}>
                    {createWidgetElement("OrderDetails/OrderPromiseDate", {} as any)}
                </GridItem>
            )}
            {order.terms && (
                <GridItem {...styles.itemInformationGridItem}>
                    {createWidgetElement("OrderDetails/OrderTerms", {} as any)}
                </GridItem>
            )}
            {order.requestedDeliveryDateDisplay && (
                <GridItem {...styles.itemInformationGridItem}>
                    {createWidgetElement("OrderDetails/OrderDetailsRequestedDate", {} as any)}
                </GridItem>
            )}
            {order.properties.shipDate && (
                <GridItem {...styles.itemInformationGridItem}>
                    {createWidgetElement("OrderDetails/OrderShipDate", {} as any)}
                </GridItem>
            )}
            {order.properties.deliveryDate && (
                <GridItem {...styles.itemInformationGridItem}>
                    {createWidgetElement("OrderDetails/OrderDeliveryDate", {} as any)}
                </GridItem>
            )}
            {order.properties.receiverName && (
                <GridItem {...styles.itemInformationGridItem}>
                    {createWidgetElement("OrderDetails/OrderReceiverName", {} as any)}
                </GridItem>
            )}
            {order.properties.transactionTypeDisplay && (
                <GridItem {...styles.itemInformationGridItem}>
                    {createWidgetElement("OrderDetails/OrderType", {} as any)}
                </GridItem>
            )}
            {order.notes && (
                <GridItem {...styles.fullWidthInformationGridItem}>
                    {createWidgetElement("OrderDetails/OrderDetailsNotes", {} as any)}
                </GridItem>
            )}
            {showPOD && (
                <GridItem {...styles.fullWidthInformationGridItem}>
                    {order.properties.transactionType === "do" ? (
                        siteMessage("McMc_PODSignature")
                    ) : (
                        <Link {...styles.signatureImgLink} onClick={showSignatureImageClickHandler}>
                            {translate("POD Signature")}
                        </Link>
                    )}
                    <Modal
                        headline={translate("POD Signature")}
                        {...styles.modal}
                        isOpen={modalIsOpen}
                        handleClose={ImageModalcancelHandler}
                    >
                        {podSignatureImg && <LazyImage src={podSignatureImg} altText="POD Signature" />}
                        {!podSignatureImg && (
                            <Notify
                                messageType="info"
                                title="Info"
                                hideCloseButton
                                body={siteMessage("McMc_NoPODImageFound")}
                            />
                        )}
                    </Modal>
                </GridItem>
            )}
            {sharedTrackingData && (
                <GridItem {...styles.pdfItemInformationGridItem}>
                    <Typography as="div" aria-labelledby="pdfGeneratedDate">
                        {translate("Shared on")}&nbsp;
                        <LocalizedDateTime
                            dateTime={new Date(sharedTrackingData.sharedOn)}
                            options={{
                                hour: "numeric",
                                minute: "numeric",
                                second: "numeric",
                            }}
                        />
                        &nbsp;
                        {translate("with")}
                        &nbsp;
                        {sharedTrackingData.toEmailAddress}
                    </Typography>
                </GridItem>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderDetailsOrderInformation),
    definition: {
        allowedContexts: [OrderDetailsPageContext],
        displayName: "Order Details Order Information",
        group: "Order Details",
    },
};

export default widgetModule;
